<div class="row m-0 table-container">
  <div class="col-12 p-0">
    <table>
      @if (tableDataAvailable) {
        <caption class="visuallyhidden">
          {{
            tableCaption
          }}
          @if (hasSortableColumns) {
            <span>{{ 'column_headers_with_buttons_are_sortable' | translate }}</span>
          }
        </caption>
      }
      <thead>
        <tr>
          @if (tableRowsAreCheckable) {
            <th [style.minWidth.px]="50" [style.width.px]="50">
              <input type="checkbox" [(ngModel)]="checkedAll" (change)="onCheckHead()" />
            </th>
          }
          @for (columnKey of columnsList; track columnKey) {
            <th [attr.aria-sort]="getTableHeaderAriaSort(columnKey)" [ngStyle]="getColumnConfig(columnKey)?.styles" scope="col">
              @if (getColumnConfig(columnKey)) {
                <div class="header-container">
                  @if (getColumnConfig(columnKey).sortingEnabled) {
                    <button
                      class="btn btn-link d-flex align-items-center p-0 fw-bold text-decoration-none text-black text-start"
                      [attr.aria-label]="getTableHeaderAriaSortLabel(columnKey)"
                      (click)="sortingTriggered(columnKey)"
                    >
                      {{ getColumnConfig(columnKey).translationKey | translate }}
                      @switch (getColumnState(columnKey).sortingOrder) {
                        @case ($sortingOrder.asc) {
                          <svg>
                            <use xlink:href="#fs-sort-up"></use>
                          </svg>
                        }
                        @case ($sortingOrder.desc) {
                          <svg>
                            <use xlink:href="#fs-sort-down"></use>
                          </svg>
                        }
                        @default {
                          <svg>
                            <use xlink:href="#fs-sort"></use>
                          </svg>
                        }
                      }
                    </button>
                  } @else {
                    {{ getColumnConfig(columnKey).translationKey ? (getColumnConfig(columnKey).translationKey | translate) : '' }}
                  }
                </div>
              }
            </th>
          }
          @if (actions?.length > 0) {
            <th scope="col" data-column="action" class="action">
              <div class="header-container">
                <p>
                  {{ 'column_header_action' | translate }}
                </p>
              </div>
            </th>
          }
        </tr>
      </thead>
      <tbody>
        @if (tableDataAvailable && rowsData.length > 0) {
          @for (tableRow of rowsData; track trackBy(rowIndex, tableRow); let rowIndex = $index) {
            <tr [ngStyle]="tableRow.styles">
              @if (tableRowsAreCheckable) {
                <td [style.minWidth.px]="50" [style.width.px]="50">
                  <input type="checkbox" [(ngModel)]="checkedRows[rowIndex]" (change)="onCheckCell(rowIndex)" />
                </td>
              }
              @for (columnKey of columnsList; track trackBy($index, columnKey)) {
                <ng-template
                  [ngTemplateOutlet]="cellContent"
                  [ngTemplateOutletContext]="{ columnKey: columnKey, rowIndex: rowIndex }"
                ></ng-template>
              }
              @if (actions?.length > 0) {
                <td [ngStyle]="getCellConfig('actions', rowIndex)?.styles" data-column="action">
                  @if (tableRow.hideActions !== true) {
                    <table-cell-dropdown [config]="getActionCellConfig(tableRow)"></table-cell-dropdown>
                  }
                </td>
              }
            </tr>
          }
          @if (actions?.length > 0) {
            <tr aria-hidden="true" class="fix-scroll-row" [style.height.rem]="actions.length * 1.8">
              <!-- Fixes vertical scroll when open "Actions" -->
              <td colspan="" [colSpan]="rowsData.length + 1"></td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>
</div>
@if (emptyState) {
  <div class="d-flex empty-state" role="status" aria-live="polite">
    <app-table-empty-state [hideBorders]="emptyStateHideBorders" [tableEmptyState]="emptyState"></app-table-empty-state>
  </div>
}

<ng-template #customColumns let-columnKey="columnKey" let-rowIndex="rowIndex">
  @if (getColumnConfig(columnKey).customType) {
    @switch (getColumnConfig(columnKey).customType) {
      @case (customColumnType.link) {
        <ng-template
          [ngTemplateOutlet]="linkedCell"
          [ngTemplateOutletContext]="{
            data: getLinkCellData(columnKey, rowIndex),
            tableLink: true,
          }"
        ></ng-template>
      }
      @case (customColumnType.linkedCell) {
        <ng-template
          [ngTemplateOutlet]="linkedCell"
          [ngTemplateOutletContext]="{
            data: getLinkCellData(columnKey, rowIndex),
          }"
        ></ng-template>
      }
      @case (customColumnType.externalLink) {
        <div class="full-width">
          <a class="table-link" rel="noreferrer noopener" target="_blank" [href]="getLinkCellData(columnKey, rowIndex).href">
            {{ getLinkCellData(columnKey, rowIndex).linkLabel }}
          </a>
        </div>
      }
      @case (customColumnType.severity) {
        <div class="severity-block severity" [ngClass]="'severity-' + getSeverityCellData(columnKey, rowIndex)">
          @if (getSeverityCellData(columnKey, rowIndex) === $severity.critical) {
            <svg aria-hidden="true" viewBox="0 0 512 512">
              <use xlink:href="#exclamation-triangle"></use>
            </svg>
          } @else {
            <svg aria-hidden="true" viewBox="0 0 512 512">
              <use xlink:href="#exclamation-circle"></use>
            </svg>
          }
          {{ 'scan_issue_severity_' + getSeverityCellData(columnKey, rowIndex) | translate }}
        </div>
      }
      @case (customColumnType.score) {
        <div class="score-container">
          @if (getScoreCellData(columnKey, rowIndex); as cellData) {
            @if (cellData?.scores?.length > 0) {
              @for (scoreResult of cellData.scores; track $index; let i = $index) {
                @if (useDsScore) {
                  <div class="score-item">
                    <ds-score [value]="scoreResult"></ds-score>
                    @if (cellData?.scoreLabels?.[i]) {
                      <span class="score-label" aria-hidden="true">({{ cellData.scoreLabels[i] | translate }})</span>
                    }
                  </div>
                } @else {
                  <!-- Using this visually hidden span to make the screen readers announce the content of this cell more clearly -->
                  <div>
                    <span class="visuallyhidden">{{ 'n_of_t' | translate: [scoreResult, 100] }}</span>
                    <span aria-hidden="true" [ngClass]="getScoreCellClass(columnKey, rowIndex)[i]" class="score-value">
                      {{ scoreResult }}
                    </span>
                    <span aria-hidden="true">/ 100</span>
                    @if (cellData?.scoreLabels?.[i]) {
                      <span aria-hidden="true"> ({{ cellData.scoreLabels[i] | translate }}) </span>
                    }
                  </div>
                }
              }
            } @else {
              @if (useDsScore && cellData.emptyStateLabel) {
                <ds-score [noValueAriaLabel]="cellData.emptyStateLabel"></ds-score>
              } @else {
                {{ cellData.emptyStateLabel }}
              }
            }
          }
        </div>
      }
      @case (customColumnType.scoreImpact) {
        <div class="score-impact-container d-flex">
          <span class="sign-icon me-1">
            {{ getScoreImpactCellData(columnKey, rowIndex) < 0 ? '–' : '+' }}
          </span>
          <span> {{ getScoreImpactCellData(columnKey, rowIndex, true) }}% </span>
        </div>
      }
      @case (customColumnType.monitoring) {
        <div class="monitoring-container">
          <app-monitoring-label [data]="getCellDataForMonitoring(columnKey, rowIndex)"></app-monitoring-label>
        </div>
      }
      @case (customColumnType.textWithTag) {
        <div class="table-tag-container">
          {{ getTextWithTagCellData(columnKey, rowIndex).text }}
          <span class="table-tag-label margin-left" [style.background]="getTextWithTagCellData(columnKey, rowIndex).tagColor">
            {{ getTextWithTagCellData(columnKey, rowIndex).tagLabel }}
          </span>
        </div>
      }
      @case (customColumnType.tooltip) {
        <div class="d-flex flex-col">
          <span class="me-5">{{ getToolTipCellData(columnKey, rowIndex).message }}</span>
          @if (getToolTipCellData(columnKey, rowIndex).hideTooltip === false) {
            <ds-chip
              class="tooltip-label"
              [text]="getToolTipCellData(columnKey, rowIndex).tooltipMessage"
              [attr.aria-label]="getToolTipCellData(columnKey, rowIndex).tooltipMessage"
              [appTooltip]="getToolTipCellData(columnKey, rowIndex).tooltipPopupMessage"
            ></ds-chip>
          }
        </div>
      }
      @case (customColumnType.action) {
        <div class="d-flex flex-col justify-content-end">
          <button class="btn btn-link btn-flat" (click)="getActionCellData(columnKey, rowIndex).action(rowsData[rowIndex])">
            {{ getActionCellData(columnKey, rowIndex).label | translate }}
          </button>
        </div>
      }
      @case (customColumnType.checkbox) {
        <div class="checkbox-container">
          <ng-template
            [ngTemplateOutlet]="checkbox"
            [ngTemplateOutletContext]="{ cellData: getCheckboxCellData(columnKey, rowIndex), rowData: rowsData[rowIndex] }"
          ></ng-template>
        </div>
      }
      @case (customColumnType.img) {
        <div>
          @if (getImageCellData(columnKey, rowIndex).embedImage) {
            <img
              [src]="getImageCellData(columnKey, rowIndex).url"
              alt="{{ 'screenshot' | translate }}"
              class="img-fluid screenshot-data"
            />
          } @else {
            {{ getImageCellData(columnKey, rowIndex).url }}
          }
        </div>
      }
      @case (customColumnType.attachments) {
        <div class="attachments-container">
          @for (attachment of getAttachmentsCellData(columnKey, rowIndex); track attachment) {
            <a href="{{ attachment.href }}" rel="noreferrer noopener" target="_blank" class="pe-2">
              @if (attachment.icon === 'attachment-document') {
                <svg>
                  <use xlink:href="#attachment-document"></use>
                </svg>
              }
              @if (attachment.icon === 'attachment-picture') {
                <svg>
                  <use xlink:href="#attachment-picture"></use>
                </svg>
              }
              <i aria-hidden="true"></i>
            </a>
          }
        </div>
      }
      @case (customColumnType.remediationProgress) {
        <div class="align-items-center remediation-progress-cell">
          @if (!getRemediationProgressCellData(columnKey, rowIndex).isArchived) {
            @if (getRemediationProgressCellData(columnKey, rowIndex).max > 0) {
              <div class="progress mt-1 mb-2">
                <div
                  class="progress-bar"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [style.width]="getRemediationProgressCellData(columnKey, rowIndex).progress + '%'"
                  [attr.aria-label]="getRemediationProgressAriaLabel(columnKey)"
                  [attr.aria-valuenow]="roundDown(getRemediationProgressCellData(columnKey, rowIndex).progress) + '%'"
                ></div>
              </div>
              <div class="count-val d-flex">
                <div class="me-auto pe-2">
                  <a
                    [routerLink]="getRemediationProgressCellData(columnKey, rowIndex).linkTo"
                    [queryParams]="getRemediationProgressCellData(columnKey, rowIndex).linkToParams || {}"
                  >
                    {{
                      'findings_remaining'
                        | translate
                          : getRemediationProgressCellData(columnKey, rowIndex).max -
                              getRemediationProgressCellData(columnKey, rowIndex).current
                    }}
                    <br />
                    ({{ getRemediationProgressCellData(columnKey, rowIndex).critical }}
                    {{ getRemediationProgressCellData(columnKey, rowIndex).criticalLabel | lowercase }})
                  </a>
                </div>
                <div class="percents text-end">
                  {{ roundDown(getRemediationProgressCellData(columnKey, rowIndex).progress) }}%
                </div>
              </div>
            } @else {
              <div>
                {{ 'label_remediation_progress_empty' | translate }}
              </div>
            }
          }
        </div>
      }
      @case (customColumnType.progress) {
        <div class="d-flex align-items-center progress-cell">
          @if (getProgressCellData(columnKey, rowIndex).max > 0) {
            <div class="progress-val">{{ roundDown(getProgressCellData(columnKey, rowIndex).progress) }}%</div>
          }
          @if (getProgressCellData(columnKey, rowIndex).max > 0) {
            <div class="progress flex-1">
              <div
                class="progress-bar"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                [style.width]="getProgressCellAsPercent(columnKey, rowIndex)"
                [attr.aria-label]="'remediation_progress_percentage' | translate"
                [attr.aria-valuenow]="getProgressCellRounded(columnKey, rowIndex)"
              ></div>
            </div>
          }
          @if (getProgressCellData(columnKey, rowIndex).max === 0) {
            <div>
              {{ 'label_remediation_progress_empty' | translate }}
            </div>
          }
        </div>
      }
      @case (customColumnType.profilePicture) {
        <div class="d-flex">
          <img [src]="getProfilePictureCellData(columnKey, rowIndex).href" alt="" class="user-profile-picture" />
          <span class="align-items-center ms-3">{{ getProfilePictureCellData(columnKey, rowIndex).message }}</span>
        </div>
      }
      @case (customColumnType.invitationStatus) {
        <ng-template
          [ngTemplateOutlet]="invitationStatusCell"
          [ngTemplateOutletContext]="{ data: getInvitationStatusCellData(columnKey, rowIndex) }"
        ></ng-template>
      }
      @case (customColumnType.forcedWordWrapText) {
        <div class="forced-word-wrap">
          {{ getCellData(columnKey, rowIndex) }}
        </div>
      }
      @case (customColumnType.select) {
        @if (getSelectCellData(columnKey, rowIndex)?.dropdownItems?.length > 0) {
          <table-cell-dropdown [config]="getSelectCellData(columnKey, rowIndex)"></table-cell-dropdown>
        } @else {
          {{ getSelectCellData(columnKey, rowIndex)?.text | translate }}
        }
      }
    }
  } @else {
    {{ getCellData(columnKey, rowIndex) }}
  }
</ng-template>

<ng-template #cellContent let-columnKey="columnKey" let-rowIndex="rowIndex">
  @if (hasCell(columnKey, rowIndex)) {
    @if (getCellConfig(columnKey, rowIndex) || {}; as cellConfig) {
      @if (getColumnConfig(columnKey).isRowHeader) {
        <th
          [colSpan]="cellConfig?.colSpan || 1"
          [ngStyle]="cellConfig?.styles"
          [scope]="(cellConfig?.colSpan || 1) > 1 ? 'rowgroup' : 'row'"
          class="custom-table-row-header-selector"
        >
          <ng-template
            [ngTemplateOutlet]="customColumns"
            [ngTemplateOutletContext]="{ columnKey: columnKey, rowIndex: rowIndex }"
          ></ng-template>
        </th>
      } @else {
        <td [colSpan]="cellConfig?.colSpan || 1" [ngStyle]="cellConfig?.styles">
          <ng-template
            [ngTemplateOutlet]="customColumns"
            [ngTemplateOutletContext]="{ columnKey: columnKey, rowIndex: rowIndex }"
          ></ng-template>
        </td>
      }
    }
  }
</ng-template>

<ng-template #linkedCell let-data="data" let-tableLink="tableLink">
  <div [class.full-width]="tableLink">
    @if (data.disabled) {
      <span>
        {{ data.linkLabel }}
      </span>
    } @else {
      <a [routerLink]="data.href" [class.table-link]="tableLink" [queryParams]="data.queryParams ? data.queryParams : {}">
        {{ data.linkLabel }}
      </a>
      @if (data.image) {
        <div class="d-flex align-items-center table-link-img-container" data-test-id="table-link-img-container">
          <img [src]="data.image.src" [alt]="data.image.alt ?? ''" class="me-2" />
          {{ data.image.label }}
        </div>
      }
      @if (data.icon) {
        <div class="d-flex align-items-center mt-2 table-link-img-container" data-test-id="table-link-icon-container">
          <ds-chip
            [text]="data.icon.label"
            [colorVariant]="data.icon.chipColorVariant || DsChipColorVariant.Info"
            [size]="data.icon.chipSize || DsChipSize.Small"
            [showIcon]="true"
            [iconPosition]="data.icon.chipIconPosition || DsChipIconPosition.Left"
            [iconName]="data.icon.icon"
            [iconStyle]="data.icon.iconStyle"
          ></ds-chip>
        </div>
      }
    }
  </div>
</ng-template>

<ng-template #checkbox let-cellData="cellData" let-rowData="rowData">
  <label class="d-flex align-items-center fw-normal">
    <input
      type="checkbox"
      class="form-element"
      [checked]="cellData?.checked(rowData)"
      [ngModelOptions]="{ standalone: true }"
      [ngModel]="cellData?.checked(rowData)"
      [indeterminate]="cellData?.indeterminate(rowData)"
      [disabled]="cellData?.disabled(rowData)"
      (ngModelChange)="cellData?.action($event, rowData)"
    />
    @if (cellData?.label(rowData)) {
      <span class="margin-left-xs">
        {{ cellData?.label(rowData) | translate }}
      </span>
    }
  </label>
</ng-template>

<ng-template #invitationStatusCell let-data="data">
  <div class="d-flex align-items-center">
    {{ 'status_' + data | translate }}
    @switch (data) {
      @case ($invitationStatus.pending) {
        <svg class="icon invitation-status-cell--icon invitation-status-pending" viewBox="0 0 20 20">
          <use xlink:href="#clock-outline"></use>
        </svg>
      }
      @case ($invitationStatus.expired) {
        <svg class="icon invitation-status-cell--icon" viewBox="0 0 21 18">
          <use xlink:href="#warning-3"></use>
        </svg>
      }
      @case ($invitationStatus.completed) {
        <svg class="icon invitation-status-cell--icon" viewBox="0 0 21 20">
          <use xlink:href="#check-outline"></use>
        </svg>
      }
    }
  </div>
</ng-template>
